import moment from 'moment';
import { getDayInString } from '../../../../../helper';
import {
  buildImagesBatchFormData,
  IMAGE_ENTITY_TYPE
} from '../../../../../components/EntityImagesGallery';
import {
  buildAmenitySchedulesFromDailyForm,
  MAX_ADVANCE_BOOKING_DAYS,
  maxAllowedAutoBlockMinutes,
  normalizeSlotDurationMinutes,
  parseScheduleHour,
  selectedDaysToCodes,
  snapAutoBlockDuration,
  WEEK_DAY_CODES
} from './addAmenityScheduleHelpersV2';
import {
  AMENITY_COST_TYPE,
  formatAmenityFeeDisplay,
  isValidCostTypeForBooking,
  parsePaymentAmountForPayload
} from './amenityPaymentConfigV2';
import {
  formatAvailabilityDisplayForCsv,
  formatAvailabilityDisplayLines
} from './amenitiesListDisplayHelpers';

export const AMENITY_WAIVER_FILE_TYPE = '10';

const REFERENCE_MONDAY_UTC = moment.utc('2000-01-03T00:00:00.000Z');

function utcIsoFromFractionalHourOfDay(fh) {
  const num = Number(fh);
  if (!Number.isFinite(num)) return null;
  if (Math.abs(num - 24) < 1e-6) {
    return moment.utc('2000-01-01T23:59:59.000Z').toISOString();
  }
  const totalMinutes = Math.round(num * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return moment
    .utc(`2000-01-01T${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:00.000Z`)
    .toISOString();
}

function attachScheduleIds(apiRows, existingSchedules = []) {
  const byDay = new Map(
    existingSchedules
      .filter((s) => s?.id != null && s?.day_of_week != null)
      .map((s) => [Number(s.day_of_week), Number(s.id)])
  );
  return apiRows.map((row) => {
    const id = byDay.get(Number(row.day_of_week));
    return id != null ? { ...row, id } : row;
  });
}

function scheduleRowsToApi(schedules) {
  return schedules.map((row) => {
    const dow = row.day_of_week;
    if (row.is_closed) {
      const day = REFERENCE_MONDAY_UTC.clone().add(dow - 1, 'days');
      return {
        day_of_week: dow,
        start_time: day.clone().startOf('day').toISOString(),
        end_time: day.clone().startOf('day').toISOString(),
        is_closed: true
      };
    }
    const startH = row.start;
    const endH = row.end;
    const dayBase = REFERENCE_MONDAY_UTC.clone().add(dow - 1, 'days');
    const startTime = dayBase.clone().add(startH, 'hours');
    let endTime;
    if (Math.abs(endH - 24) < 1e-6) {
      endTime = dayBase.clone().add(1, 'day');
    } else {
      endTime = dayBase.clone().add(endH, 'hours');
    }
    if (!endTime.isAfter(startTime)) {
      endTime = endTime.clone().add(24, 'hours');
    }
    return {
      day_of_week: dow,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      is_closed: false
    };
  });
}

function resolveMinMaxTimes(values) {
  if (!values.allow_booking) {
    return { min_time: 0, max_time: 0 };
  }
  if (values.allow_multi_day) {
    const minN = values.min_duration_value != null ? Number(values.min_duration_value) : 0;
    const maxN = values.max_duration_value != null ? Number(values.max_duration_value) : 0;
    return {
      min_time: Number.isFinite(minN) ? minN : 0,
      max_time: Number.isFinite(maxN) ? maxN : 0
    };
  }
  const minH = values.min_reservation_hours != null ? Number(values.min_reservation_hours) : 0;
  const maxH =
    values.max_reservation_hours != null ? Number(values.max_reservation_hours) : 0;
  return {
    min_time: Number.isFinite(minH) ? minH : 0,
    max_time: Number.isFinite(maxH) ? maxH : 0
  };
}

function resolveBookingLimitUnit(values) {
  if (!values.allow_booking || !values.allow_multi_day) {
    return 0;
  }
  return Number(values.booking_limits_unit);
}

function resolveAvailableBounds(values) {
  const selectedCodes = selectedDaysToCodes(values.selectedDays);
  const selectedDaySchedules = selectedCodes
    .map((code) => ({
      code,
      start: parseScheduleHour(values.daily_schedule?.[code]?.start),
      end: parseScheduleHour(values.daily_schedule?.[code]?.end)
    }))
    .filter((d) => d.start != null && d.end != null);

  if (!selectedDaySchedules.length) {
    const sRaw = Number(values.available_start);
    const eRaw = Number(values.available_end);
    const s = Number.isFinite(sRaw) ? sRaw : 0;
    const e = Number.isFinite(eRaw) ? eRaw : 24;
    return {
      available_start: utcIsoFromFractionalHourOfDay(s),
      available_end: utcIsoFromFractionalHourOfDay(e)
    };
  }

  const earliestStart = Math.min(...selectedDaySchedules.map((d) => Number(d.start)));
  const latestEnd = Math.max(...selectedDaySchedules.map((d) => Number(d.end)));

  return {
    available_start: utcIsoFromFractionalHourOfDay(earliestStart),
    available_end: utcIsoFromFractionalHourOfDay(latestEnd)
  };
}

export function buildAmenityV2CreatePayload(values, building, extras = {}) {
  const { existingSchedules = [] } = extras;

  let mySelectedDays = values.selectedDays;
  if (Array.isArray(values.selectedDays)) {
    mySelectedDays = values.selectedDays.filter((v) => v.value !== 4);
  }
  const available_days = getDayInString(mySelectedDays);

  const scheduleRows = buildAmenitySchedulesFromDailyForm(
    values.selectedDays,
    values.daily_schedule
  );
  const existingDays = new Set(
    existingSchedules.map((s) => Number(s.day_of_week))
  );
  const rowsToSend =
    existingDays.size > 0
      ? scheduleRows.filter(
          (row) => !row.is_closed || existingDays.has(row.day_of_week)
        )
      : scheduleRows.filter((row) => !row.is_closed);
  const amenities_schedules = attachScheduleIds(
    scheduleRowsToApi(rowsToSend),
    existingSchedules
  );

  const { min_time, max_time } = resolveMinMaxTimes(values);
  const booking_limit_unit = resolveBookingLimitUnit(values);
  const { available_start, available_end } = resolveAvailableBounds(values);

  const enablePost = !!values.enable_post_booking_auto_block;
  const postBlockDescription =
    enablePost &&
    values.post_block_description != null &&
    String(values.post_block_description).trim() !== ''
      ? String(values.post_block_description).trim()
      : '';

  const buildingIds =
    values.building_ids && Array.isArray(values.building_ids) && values.building_ids.length > 0
      ? values.building_ids.map((b) => parseInt(b.value, 10))
      : [];

  let advance = 0;
  if (values.allow_booking) {
    const raw = values.max_advance_booking;
    const n =
      raw != null && raw !== '' && !Number.isNaN(Number(raw)) ? Number(raw) : 30;
    advance = Math.min(MAX_ADVANCE_BOOKING_DAYS, Math.max(1, n));
  }

  const slot_duration = normalizeSlotDurationMinutes(values.slot_duration_minutes);
  const postDuration = enablePost
    ? snapAutoBlockDuration(
        values.post_booking_auto_block_duration,
        slot_duration,
        maxAllowedAutoBlockMinutes({
          bookingLimitsUnit: values.booking_limits_unit,
          allowMultiDay: !!values.allow_multi_day,
          slotMinutes: slot_duration,
          selectedDays: values.selectedDays,
          dailySchedule: values.daily_schedule,
          minDurationValue: values.min_duration_value,
          minReservationHours: values.min_reservation_hours
        })
      )
    : 0;

  const rich =
    values.rich_description != null && String(values.rich_description).trim() !== ''
      ? values.rich_description
      : '';

  const requirePayment = !!values.allow_booking && !!values.require_payment;
  const paymentFields = requirePayment
    ? {
        cost_type: isValidCostTypeForBooking(values.cost_type, values.allow_multi_day)
          ? Number(values.cost_type)
          : AMENITY_COST_TYPE.HOURLY,
        cost: parsePaymentAmountForPayload(values.payment_rate),
        service_charge: parsePaymentAmountForPayload(values.service_charge),
        deposit: parsePaymentAmountForPayload(values.deposit)
      }
    : {
        cost_type: AMENITY_COST_TYPE.NONE,
        cost: 0,
        service_charge: 0,
        deposit: 0
      };

  return {
    building_id: parseInt(building.id, 10),
    building_ids: buildingIds,
    type_id: parseInt(values.type_id, 10),
    code: (values.code || '').trim(),
    rich_description: rich,
    available_start,
    available_end,
    available_days,
    location: '',
    slot_duration,
    max_time,
    min_time,
    booking_limit_unit,
    cost: paymentFields.cost,
    cost_per_hours: 0,
    cost_type: paymentFields.cost_type,
    service_charge: paymentFields.service_charge,
    deposit: paymentFields.deposit,
    data: values.data && typeof values.data === 'object' ? values.data : {},
    allow_booking: !!values.allow_booking,
    allow_multiple_bookings_per_day: !!values.allow_same_day_multiple_bookings,
    allow_multi_day_bookings: !!values.allow_multi_day,
    require_reservation_form: !!values.require_reservation_form,
    enable_post_booking_auto_block: enablePost,
    post_booking_auto_block_duration: postDuration,
    post_block_description: postBlockDescription,
    advance_booking_limit_days: advance,
    require_manager_approval: !!values.require_manager_approval,
    require_payment: requirePayment,
    amenities_schedules
  };
}

export function buildAmenityWaiverDocFormData({
  waiverFile,
  orgId,
  buildingId,
  entityId
}) {
  const formData = new FormData();
  formData.append('doc', waiverFile);
  formData.append('type', AMENITY_WAIVER_FILE_TYPE);
  formData.append('org', orgId);
  formData.append('building', buildingId);
  formData.append('name', waiverFile.name);
  formData.append('entity_id', entityId);
  return formData;
}

export function buildAmenityImagesBatchFormData({
  amenityImages,
  amenityId,
  orgId,
  buildingId
}) {
  return buildImagesBatchFormData({
    images: amenityImages,
    entityId: amenityId,
    entityType: IMAGE_ENTITY_TYPE.AMENITY,
    orgId,
    buildingId
  });
}

function normalizeAvailabilityDisplay(raw) {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((entry) => entry != null && typeof entry === 'object')
    .map((entry) => ({
      days: entry.days != null ? String(entry.days) : '',
      time: entry.time != null ? String(entry.time) : ''
    }));
}

export function normalizeBuildingAmenitiesV2List(payload) {
  const raw = Array.isArray(payload) ? payload : payload?.data;
  if (!Array.isArray(raw)) {
    return [];
  }
  const isGrouped =
    raw.length > 0 && raw[0].amenities != null && Array.isArray(raw[0].amenities);
  const rows = isGrouped ? raw.flatMap((g) => (g.amenities || []).map((a) => ({ ...a, _groupType: g.type }))) : raw;
  return rows.map((row) => {
    const availability_display = normalizeAvailabilityDisplay(row.availability_display);
    const availabilityLines = formatAvailabilityDisplayLines(availability_display);

    return {
      id: row.id,
      description: row.description ?? row.code ?? '',
      type: row.type ?? row._groupType ?? '',
      type_id: row.type_id != null ? Number(row.type_id) : undefined,
      allow_booking: !!row.allow_booking,
      icon: typeof row.icon === 'string' ? row.icon : '',
      availability_display,
      require_manager_approval: !!row.require_manager_approval,
      require_payment: !!row.require_payment,
      cost: row.cost != null ? Number(row.cost) : null,
      cost_type: row.cost_type != null ? Number(row.cost_type) : AMENITY_COST_TYPE.NONE,
      allow_multi_day_bookings: !!row.allow_multi_day_bookings,
      building_ids: Array.isArray(row.building_ids) ? row.building_ids : [],
      building_id: row.building_id != null ? Number(row.building_id) : undefined,
      amenities_schedules: Array.isArray(row.amenities_schedules) ? row.amenities_schedules : [],
      hours_display: formatAvailabilityDisplayForCsv(availabilityLines),
      booking_type_display: row.allow_booking ? 'Reserve' : 'Drop in',
      manager_approval_display: row.require_manager_approval ? 'Yes' : 'No',
      fee_display: formatAmenityFeeDisplay({
        require_payment: !!row.require_payment,
        cost: row.cost,
        cost_type: row.cost_type
      })
    };
  });
}
